/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PrimaryButton } from "../buttons/PrimaryButton";
import Chart from "react-apexcharts";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useDropStats } from "../../store/useDropStats";
import { useMarketPricing } from "../../store/useMarketPricing";
import DropdownFilter from "../modals/DropdownFilter";

interface Props {
  selectedFiltersProp?: string[];
  className?: string;
}

export const TotalStreamPercentage = React.memo(
  ({ selectedFiltersProp, className }: Props) => {
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const { streamPercentageOfAllTime, getStreamPercentageOfAllTime } =
      useDropStats();
    const { marketPrices, getAllMarketPrices } = useMarketPricing();
    const [_, takeScreenshot] = useScreenshot();
    /** State */
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const [periodYear, setPeriodYear] = useState<number>(currentYear);

    /** Callbacks */
    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        // Create a link to download the image
        const link = document.createElement("a");
        link.href = image;
        link.download = "active_users_based_on_drops.png"; // Set the default file name
        link.click(); // Programmatically click the link to trigger download
      });
    };

    const filterOptions = useMemo(() => {
      const kgOptions = marketPrices?.filter((mp) => mp?.measurement === "kg");
      return [
        "All",
        ...(kgOptions?.length ? kgOptions?.map((price) => price.name) : []),
        "Total",
      ];
    }, [marketPrices]);

    const transformedData = useMemo(() => {
      if (!streamPercentageOfAllTime) return [];

      // Filter series based on selected filters
      const filteredSeries = streamPercentageOfAllTime.filter((series) =>
        selectedFilters.includes(series.name?.replaceAll(" Active Users", ""))
      );

      return filteredSeries;
    }, [streamPercentageOfAllTime, selectedFilters]);

    const onSelectFilters = useCallback(
      (option: string) => {
        if (option === "All") {
          if (selectedFilters.includes("All")) {
            setSelectedFilters([]);
            return;
          }
          setSelectedFilters([
            "All",
            ...(marketPrices?.length
              ? marketPrices?.map((price) => price.name)
              : []),
            "Total",
          ]);
          return;
        }
        setSelectedFilters((prev: any) =>
          prev.includes(option)
            ? prev
                .filter((item: any) => item !== option)
                ?.filter((item: any) => item !== "All")
            : [...prev, option]?.filter((item: any) => item !== "All")
        );
      },
      [marketPrices, selectedFilters, filterOptions]
    );

    /** Effects */
    useEffect(() => {
      if (selectedFiltersProp) setSelectedFilters(selectedFiltersProp);
    }, [selectedFiltersProp]);

    useEffect(() => {
      getAllMarketPrices();
    }, []);

    useEffect(() => {
      setSelectedFilters([
        "All",
        ...(marketPrices?.length
          ? marketPrices?.map((price) => price.name)
          : []),
        "Total",
      ]);
    }, [marketPrices]);

    useEffect(() => {
      getStreamPercentageOfAllTime();
    }, [periodYear]);

    return (
      <div
        className={`flex flex-col w-full sm:w-1/2 p-2 sm:p-7 bg-white border border-gray-border drop-shadow-md mb-1 sm:mb-10 ${className}`}
      >
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
            Total Stream Percentage
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
            <DropdownFilter
              label="Filters"
              options={filterOptions}
              selectedOptions={selectedFilters}
              onSelect={onSelectFilters}
            />
            <div className="ml-0 sm:ml-2" />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
            />
          </div>
        </div>
        <div ref={snapshotRef}>
          {transformedData?.length ? (
            <Chart
              // @ts-ignore
              options={{
                ...chartConfig.options,
                labels: transformedData.map((item) => item.name),
              }}
              series={transformedData.map((item) => item.data[0])}
              type="pie"
              height={350}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
                No data found
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

const chartConfig = {
  options: {
    chart: {
      height: 240,
      type: "pie",
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val.toFixed(1) + "%";
      },
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
    },
    colors: [
      "#23C16B", // Green
      "#FFB323", // Yellow
      "#48A7F8", // Blue
      "#FF3333", // Red
      "#FF8800", // Orange
      "#9259FF", // Purple
      "#FF4DA6", // Pink
      "#33FFE6", // Teal
      "#808080", // Gray
      "#D17A50", // Brown
    ],
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  },
};
